import { useEffect, useState } from "react";
import { Calculator, MapPin, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import AddressAutocomplete from "./AddressAutocomplete";
import { quoteManualTow, quoteAaTow, quoteSnTow } from "../lib/api";

/**
 * Driver-only Price Check dialog — Feb 2026.
 *
 * Opened from the on-call driver header for ad-hoc after-hours price
 * requests ("How much to tow my van home from Takaka?"). Supports all
 * three bases (AA / First Rescue / Manual). Still no settings gear —
 * admin manages rates on the desktop /tow-calc page and Settings page.
 */

const BASES = [
  { k: "manual", label: "Manual" },
  { k: "aa", label: "AA" },
  { k: "fr", label: "FR" },
];

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

// Collapse a provider quote (AA / FR) into the same totals shape as
// the manual quote so the readout section can stay basis-agnostic.
// Provider endpoints return a flat `price.total` which is ex-GST on
// AA, and ex-GST on SN too. GST of 15% is applied client-side.
const totalsFromProvider = (price) => {
  const ex = Number(price?.total ?? 0);
  const inc = Number((ex * 1.15).toFixed(2));
  return { ex_gst: ex, gst_amount: Number((inc - ex).toFixed(2)), inc_gst: inc };
};

export default function DriverPriceCheckDialog({ open, onOpenChange }) {
  const [basis, setBasis] = useState("manual");
  const [pickup, setPickup] = useState({ label: "", lat: null, lng: null });
  const [drop, setDrop] = useState({ label: "", lat: null, lng: null });
  // Heavy vehicle / truck tick — Manual basis only, mirrors the desktop
  // Tow Calc. Lets a driver on the side of the road quote a 4-axle
  // correctly without calling dispatch.
  const [heavy, setHeavy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Reset when the dialog closes so each caller starts fresh.
  useEffect(() => {
    if (!open) {
      setBasis("manual");
      setPickup({ label: "", lat: null, lng: null });
      setDrop({ label: "", lat: null, lng: null });
      setHeavy(false);
      setQuote(null);
      setErr("");
      setLoading(false);
    }
  }, [open]);

  const canQuote = pickup.lat && pickup.lng && drop.lat && drop.lng;

  // Auto re-quote on input or basis change (debounced).
  useEffect(() => {
    if (!open) return;
    if (!canQuote) {
      setQuote(null);
      setErr("");
      return;
    }
    setLoading(true);
    setErr("");
    const t = setTimeout(async () => {
      try {
        const iso = new Date().toISOString();
        const common = {
          pickup_lat: pickup.lat,
          pickup_lng: pickup.lng,
          drop_lat: drop.lat,
          drop_lng: drop.lng,
          at: iso,
        };
        if (basis === "manual") {
          const r = await quoteManualTow({ ...common, heavy_vehicle: heavy });
          setQuote({ basis: "manual", ...r });
        } else {
          // AA/FR need distance_km only — route via manual quote (which
          // calls OSRM) to get the round-trip km, then feed the partner
          // quote endpoint. Heavy flag ignored on provider bases.
          const route = await quoteManualTow(common);
          const providerFn = basis === "aa" ? quoteAaTow : quoteSnTow;
          const body = { distance_km: route.distance_km, at: iso };
          if (basis === "fr") body.service_type = "tow";
          const price = await providerFn(body);
          setQuote({
            basis,
            distance_km: route.distance_km,
            billable_minutes: route.billable_minutes,
            is_after_hours: route.is_after_hours,
            totals: totalsFromProvider(price),
          });
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "Quote failed");
        setQuote(null);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [open, basis, pickup.lat, pickup.lng, drop.lat, drop.lng, heavy, canQuote]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md max-h-[92vh] overflow-y-auto"
        data-testid="driver-price-check-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-emerald-600" />
            Price Check
          </DialogTitle>
          <DialogDescription>
            Quick after-hours quote from your depot — round-trip, incl. GST.
          </DialogDescription>
        </DialogHeader>

        {/* Basis tabs */}
        <div className="flex gap-2" data-testid="driver-pricecheck-basis-tabs">
          {BASES.map((b) => (
            <button
              key={b.k}
              type="button"
              onClick={() => setBasis(b.k)}
              data-testid={`driver-pricecheck-basis-${b.k}`}
              className={
                basis === b.k
                  ? "flex-1 px-3 py-2 rounded-md bg-slate-900 text-white font-semibold text-sm dark:bg-slate-100 dark:text-slate-900"
                  : "flex-1 px-3 py-2 rounded-md bg-slate-100 text-slate-600 font-semibold text-sm hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700"
              }
            >
              {b.label}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs flex items-center gap-1">
              <MapPin className="h-3 w-3 text-emerald-600" /> Pickup
            </Label>
            <AddressAutocomplete
              value={pickup.label}
              onChange={(v) => setPickup({ ...pickup, label: v })}
              onPick={(p) => setPickup(p)}
              placeholder="Pickup address"
              testIdPrefix="driver-pricecheck-pickup"
            />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1">
              <MapPin className="h-3 w-3 text-red-600" /> Drop-off
            </Label>
            <AddressAutocomplete
              value={drop.label}
              onChange={(v) => setDrop({ ...drop, label: v })}
              onPick={(p) => setDrop(p)}
              placeholder="Drop-off address"
              testIdPrefix="driver-pricecheck-drop"
            />
            <label
              className={
                "mt-2 inline-flex items-center gap-2 text-xs select-none " +
                (basis === "manual" ? "cursor-pointer text-slate-700 dark:text-slate-300" : "cursor-not-allowed text-slate-400 dark:text-slate-500")
              }
              data-testid="driver-pricecheck-heavy-label"
            >
              <input
                type="checkbox"
                checked={heavy}
                disabled={basis !== "manual"}
                onChange={(e) => setHeavy(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                data-testid="driver-pricecheck-heavy-checkbox"
              />
              Heavy Vehicle/Motorhome/Caravan
              {basis !== "manual" && (
                <span className="text-[11px] italic text-slate-400">(Manual only)</span>
              )}
            </label>
          </div>
        </div>

        {err && (
          <div
            className="rounded-md border border-red-200 bg-red-50 text-red-800 p-2.5 text-sm dark:bg-red-950/40 dark:border-red-900/50 dark:text-red-200"
            data-testid="driver-pricecheck-error"
          >
            {err}
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Routing &amp; pricing…
          </div>
        )}

        {quote && !loading && (
          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900/40 p-4 space-y-2"
            data-testid="driver-pricecheck-quote"
          >
            <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400">
              <span>Round-trip</span>
              <span className="font-mono">{quote.distance_km} km</span>
            </div>
            <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400">
              <span>{quote.is_after_hours ? "After-hours" : "Business-hours"}</span>
              <span className="font-mono">
                {Math.round(quote.billable_minutes || 0)} min billable
              </span>
            </div>
            <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400 pt-1 border-t border-emerald-200/60 dark:border-emerald-900/40">
              <span>Total ex-GST</span>
              <span className="font-mono">{fmt(quote.totals.ex_gst)}</span>
            </div>
            <div className="flex justify-between items-center pt-1 border-t border-emerald-200/60 dark:border-emerald-900/40">
              <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                Total inc-GST
              </span>
              <span
                className="font-mono font-black text-2xl text-emerald-700 dark:text-emerald-300"
                data-testid="driver-pricecheck-total"
              >
                {fmt(quote.totals.inc_gst)}
              </span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            data-testid="driver-pricecheck-close-btn"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
